import { useState } from "react";

import ContentLayout from "@/layouts/ContentLayout";

import { TabMenu } from "@/types/Shared/tab";

import { useUsageStatistics } from "@/libs/react-query/hooks/useUsageQuery";
import { useIsProductManager } from "@/hooks/useIsFinancialManager";

import CommonTab from "@/components/Common/Tab";
import DashboardSummaryCard from "@/components/DashboardSummaryCard";
import DashboardCardHeader from "@/components/DashboardCardHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import UsageStatusDistribution from "./components/UsageStatusDistribution";
import UsageBreakdownCard from "./components/UsageBreakdownCard";
import UsageActivityCharts from "./components/UsageActivityCharts";
import UsageDateFilter, {
  DefaultUsageDateRange,
  UsageDateRange,
} from "./components/UsageDateFilter";

export default function UsageDashboard() {
  const isProductManager = useIsProductManager();

  // 설정 페이지(src/pages/Setting)와 동일한 탭 구성
  const tabData: TabMenu[] = [
    {
      path: "/setting",
      label: "주식회사 써모피셔 사이언티픽",
    },
    ...(isProductManager
      ? [{ path: "/setting/substitute", label: "대체품 목록" }]
      : []),
    // 이 페이지는 AdminGuard 하위라 항상 어드민
    {
      path: "/setting/usage",
      label: "사용 현황",
    },
  ];

  const [dateRange, setDateRange] = useState<UsageDateRange>(
    DefaultUsageDateRange
  );

  const { data, isLoading, isError, refetch } = useUsageStatistics({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
  });

  const summaryTiles = [
    { title: "입찰관리 총 건수", content: data?.bid_management.total },
    { title: "산출내역서 총 생성", content: data?.itemsheets.total },
    { title: "시나리오 생성", content: data?.scenarios.total },
    { title: "시나리오 승인", content: data?.approvals.approved },
    { title: "오늘 DAU", content: data?.activity.dau_today },
    { title: "이번 달 MAU", content: data?.activity.mau_this_month },
  ];

  return (
    <ContentLayout>
      <CommonTab variants="string" data={tabData} />
      <UsageDateFilter value={dateRange} onChange={setDateRange} />
      {isError ? (
        <div className="flex-1 flex flex-col justify-center items-center gap-4">
          <div className="text-gray-A text-sm">
            사용 현황을 불러오지 못했습니다.
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex gap-5">
            {summaryTiles.map((tile) =>
              isLoading ? (
                <Skeleton key={tile.title} className="flex-1 h-[108px]" />
              ) : (
                <DashboardSummaryCard
                  key={tile.title}
                  title={tile.title}
                  content={tile.content ?? 0}
                />
              )
            )}
          </div>
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <DashboardCardHeader
                title="입찰관리 상태 분포"
                isLoading={isLoading}
              >
                <UsageStatusDistribution
                  statuses={data?.bid_management.statuses ?? []}
                  total={data?.bid_management.total ?? 0}
                />
              </DashboardCardHeader>
            </div>
            <div className="flex flex-col gap-5">
              {isLoading ? (
                <>
                  <Skeleton className="flex-1" />
                  <Skeleton className="flex-1" />
                </>
              ) : (
                <>
                  <UsageBreakdownCard
                    title="산출내역서"
                    total={data?.itemsheets.total ?? 0}
                    items={[
                      {
                        label: "완료",
                        value: data?.itemsheets.completed ?? 0,
                        color: "#00A26B",
                      },
                      {
                        label: "실패",
                        value: data?.itemsheets.failed ?? 0,
                        color: "#E13F4A",
                      },
                      {
                        label: "진행중",
                        value: data?.itemsheets.in_progress ?? 0,
                        color: "#4765B0",
                      },
                    ]}
                  />
                  <UsageBreakdownCard
                    title="시나리오 승인"
                    total={data?.approvals.total ?? 0}
                    items={[
                      {
                        label: "승인",
                        value: data?.approvals.approved ?? 0,
                        color: "#00A26B",
                      },
                      {
                        label: "반려",
                        value: data?.approvals.rejected ?? 0,
                        color: "#E13F4A",
                      },
                      {
                        label: "취소",
                        value: data?.approvals.canceled ?? 0,
                        color: "#BFBFBF",
                      },
                      {
                        label: "미결",
                        value: data?.approvals.undecided ?? 0,
                        color: "#FEBF5A",
                      },
                    ]}
                  />
                </>
              )}
            </div>
          </div>
          <UsageActivityCharts
            activity={data?.activity}
            isLoading={isLoading}
            startDate={dateRange.start_date}
            endDate={dateRange.end_date}
          />
        </div>
      )}
    </ContentLayout>
  );
}
